import React, { useEffect } from 'react';
import { useGlobalAction, useUser } from '@gadgetinc/react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { hasRole, normalizeRoles } from '../lib/roleUtils';

const Agents = () => {
  const navigate = useNavigate();
  const user = useUser(api);
  
  const [{ data: vmAgentsData, fetching, error }, listVMAgents] = useGlobalAction(api.listVMAgents);

  // Fetch VM agents on component mount
  useEffect(() => {
    listVMAgents();
  }, []);

  // Extract agents array from the response data
  const agentConfigs = vmAgentsData?.agents || [];

  const formatAgentNameForURL = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9-]/g, '');
  };

  const handleStartChat = (agent) => {
    // Use standard agent routing format
    navigate(`/agents/${formatAgentNameForURL(getAgentDisplayName(agent))}`);
  };

  const handleCreateAgent = () => {
    navigate('/create-agent');
  };

  const getAgentDisplayName = (agent) => {
    return agent.name || agent.id || 'Unknown Agent';
  };

  const getAgentDescription = (agent) => {
    // Handle richText objects with markdown property
    const extractText = (value) => {
      if (!value) return null;
      if (typeof value === 'string') return value;
      if (typeof value === 'object' && value.markdown) return value.markdown;
      return null;
    };

    const description = extractText(agent.description);
    
    return description || 'VM-based AI assistant for medical education and training';
  };

  // Check if current user can create agents
  const canCreateAgent = () => {
    if (!user) return false;
    
    // Check if user has isCreator flag
    if (user.isCreator) return true;
    
    // Check if user has admin or system-admin roles
    if (user.roles && Array.isArray(user.roles)) {
      return hasRole(user, 'system-admin') || hasRole(user, 'admin');
    }
    
    return false;
  };

  const formatCapabilities = (agent) => {
    const capabilities = [];
    
    // Extract capabilities from VM agent data structure
    if (agent.tools && Array.isArray(agent.tools)) {
      agent.tools.forEach(tool => {
        if (tool.type) {
          capabilities.push(`${tool.type} support`);
        }
        if (tool.function && tool.function.name) {
          capabilities.push(tool.function.name);
        }
      });
    }
    
    if (agent.model) {
      capabilities.push(`Model: ${agent.model}`);
    }
    
    if (agent.temperature !== undefined) {
      capabilities.push(`Temperature: ${agent.temperature}`);
    }
    
    // Add default capabilities if none found
    if (capabilities.length === 0) {
      capabilities.push('Conversational AI', 'Medical Knowledge', 'Educational Support');
    }
    
    return capabilities;
  };

  if (fetching && !vmAgentsData) {
    return (
      <div className="agents-page">
        <div className="page-header">
          <h1>VM AI Agents</h1>
          <p>Connecting to VM server and loading available agents...</p>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Establishing connection to VM agent server...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isVMConnectionError = error.message?.toLowerCase().includes('vm') || 
                               error.message?.toLowerCase().includes('connection') ||
                               error.message?.toLowerCase().includes('timeout');
    
    return (
      <div className="agents-page">
        <div className="page-header">
          <h1>VM AI Agents</h1>
        </div>
        <div className="error-state">
          <h3>{isVMConnectionError ? 'VM Server Connection Failed' : 'Unable to Load Agents'}</h3>
          <p>
            {isVMConnectionError 
              ? 'Could not connect to the VM agent server. Please check your connection and try again.'
              : 'There was an error loading the available agents from the VM server.'
            }
          </p>
          <p className="error-details">{error.message}</p>
          <button 
            className="retry-btn"
            onClick={() => listVMAgents()}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!agentConfigs || agentConfigs.length === 0) {
    return (
      <div className="agents-page">
        <div className="page-header">
          <h1>VM AI Agents</h1>
          {canCreateAgent() && (
            <div className="header-actions">
              <button 
                className="create-agent-btn"
                onClick={handleCreateAgent}
              >
                Create New Agent
              </button>
            </div>
          )}
        </div>
        <div className="empty-state">
          <h3>No VM Agents Available</h3>
          <p>There are currently no active agents running on the VM server.</p>
          {canCreateAgent() ? (
            <>
              <p>Create your first agent to get started with VM-based AI assistance.</p>
              <button 
                className="create-agent-btn-large"
                onClick={handleCreateAgent}
              >
                Create Your First Agent
              </button>
            </>
          ) : (
            <p>Please contact an administrator to create agents for your institution.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="agents-page">
      <div className="page-header">
        <div className="breadcrumb">
          <Link to="/">Dashboard</Link> → <span>VM AI Agents</span>
        </div>
        <div className="header-content">
          <div className="header-text">
            <h1>VM AI Agents</h1>
            <p>Choose a VM-powered AI assistant to help with your medical education and training</p>
          </div>
          {canCreateAgent() && (
            <div className="header-actions">
              <button 
                className="create-agent-btn"
                onClick={handleCreateAgent}
              >
                Create New Agent
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="agents-grid">
        {agentConfigs.map((agent) => {
          const capabilities = formatCapabilities(agent);
          const isActive = true; // VM agents are considered active if returned by the API
          
          return (
            <div key={agent.id} className="agent-card">
              <div className="agent-card-header">
                <h3 className="agent-name">{getAgentDisplayName(agent)}</h3>
                <div className="agent-status">
                  <span className="status-indicator active">
                    VM Active
                  </span>
                </div>
              </div>
              
              <div className="agent-description">
                <p>{getAgentDescription(agent)}</p>
              </div>

              {capabilities.length > 0 && (
                <div className="agent-capabilities">
                  <h4>Capabilities:</h4>
                  <ul>
                    {capabilities.slice(0, 4).map((capability, index) => (
                      <li key={index}>{capability}</li>
                    ))}
                    {capabilities.length > 4 && (
                      <li className="more-indicator">+{capabilities.length - 4} more...</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="agent-info">
                {agent.created_at && (
                  <p className="agent-date">Created: {new Date(agent.created_at * 1000).toLocaleDateString()}</p>
                )}
                {agent.model && (
                  <p className="agent-model">Model: {agent.model}</p>
                )}
              </div>

              <div className="agent-actions">
                <button 
                  className="start-chat-btn"
                  onClick={() => handleStartChat(agent)}
                >
                  Start VM Chat
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <style jsx={true}>{`
        .agents-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .page-header {
          margin-bottom: 2rem;
        }

        .breadcrumb {
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 1rem;
        }

        .breadcrumb a {
          color: #2563eb;
          text-decoration: none;
        }

        .breadcrumb a:hover {
          text-decoration: underline;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 2rem;
        }

        .header-text {
          flex: 1;
        }

        .header-text h1 {
          font-size: 2.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
          font-weight: 700;
        }

        .header-text p {
          font-size: 1.1rem;
          color: #6b7280;
          margin: 0;
        }

        .header-actions {
          flex-shrink: 0;
        }

        .create-agent-btn {
          background: #10b981;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .create-agent-btn:hover {
          background: #059669;
        }

        .create-agent-btn-large {
          background: #10b981;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 1rem 2rem;
          font-size: 1.1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-top: 1rem;
        }

        .create-agent-btn-large:hover {
          background: #059669;
        }

        .agents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        @media (min-width: 768px) {
          .agents-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .agents-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (min-width: 1280px) {
          .agents-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        .agent-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .agent-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .agent-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }

        .agent-name {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          flex: 1;
        }

        .agent-status {
          margin-left: 1rem;
        }

        .status-indicator {
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .status-indicator.active {
          background-color: #dcfce7;
          color: #166534;
        }

        .status-indicator.inactive {
          background-color: #fef2f2;
          color: #991b1b;
        }

        .agent-description {
          margin-bottom: 1rem;
          flex: 1;
        }

        .agent-description p {
          color: #4b5563;
          line-height: 1.5;
          margin: 0;
        }

        .agent-capabilities {
          margin-bottom: 1.5rem;
        }

        .agent-capabilities h4 {
          font-size: 0.9rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .agent-capabilities ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .agent-capabilities li {
          font-size: 0.85rem;
          color: #6b7280;
          padding: 0.25rem 0;
          border-left: 3px solid #e5e7eb;
          padding-left: 0.75rem;
        }

        .agent-capabilities li.more-indicator {
          font-style: italic;
          color: #9ca3af;
        }

        .agent-actions {
          margin-top: auto;
        }

        .agent-info {
          margin-bottom: 1rem;
        }

        .agent-info p {
          font-size: 0.8rem;
          color: #6b7280;
          margin: 0.25rem 0;
        }

        .agent-date {
          font-style: italic;
        }

        .agent-model {
          font-weight: 500;
        }

        .start-chat-btn {
          width: 100%;
          background: #2563eb;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .start-chat-btn:hover {
          background: #1d4ed8;
        }

        .retry-btn {
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-top: 1rem;
        }

        .retry-btn:hover {
          background: #dc2626;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 200px;
          gap: 1rem;
        }

        .loading-spinner p {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-state, .empty-state {
          text-align: center;
          padding: 3rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          margin-top: 2rem;
        }

        .error-state h3, .empty-state h3 {
          color: #1f2937;
          margin-bottom: 1rem;
        }

        .error-state p, .empty-state p {
          color: #6b7280;
          margin-bottom: 0.5rem;
        }

        .error-details {
          font-size: 0.9rem;
          color: #ef4444;
          font-family: monospace;
          background: #fef2f2;
          padding: 1rem;
          border-radius: 6px;
          margin-top: 1rem;
        }
      `}</style>
    </div>
  );
};

export default Agents;